import type { PresentationAppearance } from './presentation-appearance.js';

/** Frozen placement guidance used by both the browser composition and render jobs. */
export const PRESENTATION_VERSION = 1;
export const GUIDE_VERSION = "2026-10-02";
export const SOCIAL_TARGETS = ["instagram-feed", "instagram-reels", "instagram-stories", "tiktok", "youtube-shorts"] as const;
export type SocialTarget = (typeof SOCIAL_TARGETS)[number];
export type PresentationTarget = SocialTarget | "none";
export const PRESENTATION_LAYOUTS = ["compact", "map-focus", "original"] as const;
export type PresentationLayout = (typeof PRESENTATION_LAYOUTS)[number];
export interface LegacyVideoPresentation extends PresentationAppearance {
  readonly version: 1;
  readonly guideVersion: typeof GUIDE_VERSION;
  readonly target: PresentationTarget;
  readonly layout: PresentationLayout;
  readonly titlePosition: "upper" | "above-statistics" | "hidden";
  readonly titleScale: number;
  readonly statisticsScale: number;
  readonly statisticsLayout: "four-columns" | "two-by-two";
  readonly showTotals: boolean;
  readonly showElevation: boolean;
  readonly showRideDetails: boolean;
}
export type { SocialVideoPresentation, SocialLayout } from './social-presentation.js';
import type { SocialVideoPresentation } from './social-presentation.js';
export type VideoPresentation = LegacyVideoPresentation | SocialVideoPresentation;
export interface Insets { readonly top: number; readonly bottom: number; readonly left: number; readonly right: number }
export interface Rectangle { readonly x: number; readonly y: number; readonly width: number; readonly height: number }
export interface PlacementGuide {
  readonly label: string;
  readonly evidence: "ad-guidance" | "estimate";
  readonly sources: readonly string[];
  readonly verifiedAt: typeof GUIDE_VERSION;
  readonly insets: Insets;
}
const metaInsets: Insets = { top: .14, bottom: .35, left: .06, right: .06 };
export const PLACEMENT_GUIDES: Readonly<Record<SocialTarget, PlacementGuide>> = {
  "instagram-feed": { label: "Instagram Feed", evidence: "ad-guidance", verifiedAt: GUIDE_VERSION, insets: metaInsets,
    sources: ["https://www.facebook.com/business/help/980593475366490"] },
  "instagram-reels": { label: "Instagram Reels", evidence: "ad-guidance", verifiedAt: GUIDE_VERSION, insets: metaInsets,
    sources: ["https://www.facebook.com/business/ads-guide/update/video/instagram-reels"] },
  "instagram-stories": { label: "Instagram Stories", evidence: "ad-guidance", verifiedAt: GUIDE_VERSION, insets: metaInsets,
    sources: ["https://www.facebook.com/business/ads-guide/update/video/instagram-story"] },
  tiktok: { label: "TikTok", evidence: "estimate", verifiedAt: GUIDE_VERSION,
    insets: { top: .10, bottom: .35, left: .06, right: .20 },
    sources: ["https://ads.tiktok.com/resources/help/article/tiktok-auction-in-feed-ads?lang=en"] },
  "youtube-shorts": { label: "YouTube Shorts", evidence: "ad-guidance", verifiedAt: GUIDE_VERSION,
    insets: { top: 288 / 1920, bottom: 672 / 1920, left: 48 / 1080, right: 192 / 1080 },
    sources: ["https://support.google.com/google-ads/answer/9128498?hl=en"] },
};
const noInsets: Insets = { top: 0, bottom: 0, left: 0, right: 0 };

/** Layout defaults are versioned so queued jobs retain the composition they requested. */
export function createVideoPresentation(target: PresentationTarget, layout: PresentationLayout = "compact"): LegacyVideoPresentation {
  const original = layout === "original" || target === "none";
  const mapFocus = !original && layout === "map-focus";
  return { version: PRESENTATION_VERSION, guideVersion: GUIDE_VERSION, target,
    layout: original ? "original" : layout,
    titlePosition: mapFocus ? "above-statistics" : "upper",
    titleScale: original ? 100 : mapFocus ? 80 : 90,
    statisticsScale: original ? 100 : mapFocus ? 80 : 90,
    statisticsLayout: original || mapFocus ? "four-columns" : "two-by-two",
    showTotals: !mapFocus, showElevation: !mapFocus, showRideDetails: !mapFocus };
}
export const LEGACY_PRESENTATION = createVideoPresentation("none", "original");

/** Nonvertical Feed margins are working estimates, including account/audio protection. */
export function placementInsets(target: PresentationTarget, width: number, height: number): Insets {
  if (target === "none") return noInsets;
  if (target === "instagram-feed" && width / height > .6) return { top: .14, bottom: .08, left: .06, right: .06 };
  return PLACEMENT_GUIDES[target].insets;
}
export function clearRectangle(target: PresentationTarget, width: number, height: number): Rectangle {
  const insets = placementInsets(target, width, height);
  return { x: insets.left * width, y: insets.top * height,
    width: width * (1 - insets.left - insets.right), height: height * (1 - insets.top - insets.bottom) };
}
export function presentationSupportsPreset(presentation: VideoPresentation, preset: string): boolean {
  return presentation.target === "none" || preset === "vertical-9x16"
    || (presentation.target === "instagram-feed" && preset === "social-4x5");
}
export function rectangleInside(inner: Rectangle, outer: Rectangle, tolerance = 1): boolean {
  return inner.x >= outer.x - tolerance && inner.y >= outer.y - tolerance
    && inner.x + inner.width <= outer.x + outer.width + tolerance
    && inner.y + inner.height <= outer.y + outer.height + tolerance;
}
